/**
 * Typed text primitive.
 *
 * Every string in the app renders through this. A raw `<Text>` with an inline
 * fontSize would bypass the type scale and the "footer never larger than
 * header" rule that the theme enforces.
 */

import React, { memo } from 'react';
import { Platform, Text as RNText, StyleSheet, type TextProps as RNTextProps } from 'react-native';

import { colors, textStyles, type TextRole } from '../../theme';
import { useLanguage } from '../../core/i18n/LanguageProvider';
import { platformSwapsSides } from '../../core/i18n/nativeDirection';

type ToneName = 'primary' | 'secondary' | 'tertiary' | 'disabled' | 'brand' | 'onBrand' | 'onBrandMuted' | 'danger';

export interface TextProps extends RNTextProps {
  /** Role from the type scale. Defaults to body copy. */
  variant?: TextRole;
  /** Semantic colour role. Defaults to primary body colour. */
  tone?: ToneName;
  /**
   * `start` and `end` follow the app's language — the side a line of Urdu
   * begins on is the right — whatever the words themselves are, so a Latin
   * name or a time in an Urdu screen sits with the Urdu around it.
   */
  align?: 'left' | 'center' | 'right' | 'start' | 'end';
  /**
   * A font size worked out for the space available, in place of the role's
   * own. The role's line spacing scales with it, Urdu's extra height included,
   * so a caller never sets a line height by hand.
   */
  size?: number;
  children?: React.ReactNode;
}

const toneColor: Record<ToneName, string> = {
  primary: colors.text.primary,
  secondary: colors.text.secondary,
  tertiary: colors.text.tertiary,
  disabled: colors.text.disabled,
  brand: colors.text.brand,
  onBrand: colors.text.onBrand,
  onBrandMuted: colors.text.onBrandMuted,
  danger: colors.text.danger,
};

/** A logical alignment as the physical side each platform needs to be told. */
/**
 * Whether a text's alignment comes out mirrored, so the side meant has to be
 * given as its opposite.
 *
 * Android swaps once the app is right-to-left natively (platformSwapsSides).
 * iOS swaps a text's left and right whenever its layout runs right to left —
 * which ScreenShell's, the drawer's and the calendar's `direction` style
 * make it in Urdu (React Native's RCTTextAttributes) — though it does not
 * swap positions, so this is for text only. The web never swaps.
 */
function textSwapsSides(isRTL: boolean): boolean {
  return Platform.OS === 'ios' ? isRTL : platformSwapsSides();
}

function resolveAlign(
  align: TextProps['align'],
  isRTL: boolean,
): 'left' | 'center' | 'right' | undefined {
  // Words in a right-to-left language start at the right even when no
  // alignment is asked for. Android and the web do that by themselves, but
  // iOS lines unaligned text up by the app's own direction — left, since the
  // app mirrors its layout with a style rather than turning iOS right to
  // left — and every Urdu sentence on an iPhone sat against the left edge.
  if (align === undefined && isRTL) return resolveAlign('start', isRTL);
  if (align !== 'start' && align !== 'end') return align;
  const right = (align === 'start') === isRTL;
  return right !== textSwapsSides(isRTL) ? 'right' : 'left';
}

/**
 * The least line height Urdu takes, as a multiple of the font size: room for
 * the script's descenders and dots. A minimum rather than a multiplier — a
 * role already spaced wider than this keeps its own spacing, instead of
 * running to more than twice the font size, as the hero's and the cards'
 * sentences did when every role was scaled up by half.
 */
const URDU_LINE_HEIGHT = 1.75;

export const Text = memo(function Text({
  variant = 'body',
  tone = 'primary',
  align,
  size,
  style,
  children,
  ...rest
}: TextProps) {
  // A price or a date formatted by shared code has no other reason to
  // re-render when the language changes, and context reaches consumers
  // through memoised parents.
  const { language, isRTL } = useLanguage();
  const textAlign = resolveAlign(align, isRTL);

  const role = textStyles[variant];
  const fontSize = size ?? role.fontSize;
  const lineHeight = (role.lineHeight / role.fontSize) * fontSize;

  return (
    <RNText
      style={StyleSheet.flatten([
        role,
        size === undefined ? null : { fontSize, lineHeight: Math.round(lineHeight) },
        // Urdu sits taller than Latin in the same point size — its descenders
        // and dots reach below the baseline — so a line height chosen for
        // English clips it. The weekday row in the calendar showed it first.
        language === 'ur'
          ? { lineHeight: Math.round(Math.max(lineHeight, fontSize * URDU_LINE_HEIGHT)) }
          : null,
        { color: toneColor[tone] },
        textAlign ? { textAlign } : null,
        style,
      ])}
      // Cap scaling so a large accessibility font cannot break the hero into
      // a clipped mess; it still grows, just not without bound.
      maxFontSizeMultiplier={1.6}
      {...rest}
    >
      {children}
    </RNText>
  );
});
